import React, { useRef, useState, useMemo } from 'react';
import { ResponsiveSunburst } from '@nivo/sunburst';
import ChartCard from '../shared/ChartCard.js';
import {
    BREAKDOWN_GRADIENT,
    colorsAlongGradient,
    OTHER_COLOR,
    DIM_TARGET,
    FONT_STACK,
    EQUIPMENT_TYPE_LABELS,
    RATING_LETTER_COLORS,
    formatValue,
    mixColor,
    subcategoryColor,
} from '../shared/chartTheme.js';
import '../../styles/DashboardComponents.css';
import '../../styles/ClientDashboard.css';

const CHART_RADIUS = 160; // même échelle que le chartSize par défaut du mode Export
const CHART_MARGIN = 40;

// node.path vient de d3 ancestors() : [soi-même, parent, ..., racine]. Pour un nœud de
// catégorie (depth 1) path[1] vaut donc "root" et non son propre id — il faut
// distinguer les deux profondeurs pour retrouver la catégorie d'appartenance.
const categoryIdOf = (node) => (node.depth === 1 ? node.id : node.path[1]);

/* ─────────────────────────────────────────────
   Mode Interactif de "Répartition par catégorie d'usage" : aucun label sur le donut
   lui-même (aplats de couleur + tooltip au survol seulement) — toute l'information
   textuelle vit dans la légende à droite, qui est le vrai centre d'interaction :
   - survoler une ligne de légende éclaire la portion correspondante du donut, et
     inversement (chart ↔ légende partagent le même état `hovered`/`pinned`) ;
   - cliquer une sous-catégorie (sur le donut ou dans la légende) déplie sous sa ligne
     la liste de ses usages (points d'eau qui la composent), scrollable, avec le même
     habillage que la liste "Points d'eau" de ConsumptionBreakdownDetail
     (.cbd-point-*) — un seul dépliement actif à la fois.

   @param {{id:string, label:string, value:number}[]} categoryTotals
   @param {{id:string, categoryId:string, label:string, value:number}[]} subcategoryTotals
   @param {object[]} waterPointRows - enrichi (usage_category_label/usage_subcategory_label,
          cf. ClientBuildingDashboard.js), nécessaire pour le dépliement par sous-catégorie
   @param {string} unit
   @param {React.ReactNode} [headerActions] - ex. le bouton de bascule Interactif/Export

   Réutilisé tel quel pour « Répartition par section et pièce » (anneau intérieur =
   sections, extérieur = pièces) via les props ci-dessous ; leurs valeurs par défaut
   donnent le comportement « catégorie d'usage ».
   @param {string} [title]
   @param {string} [innerEyebrow] - surtitre du centre quand un nœud intérieur est actif
   @param {(sub:object, rows:object[]) => object[]} [getRowsForSub] - points d'eau dépliés
          sous un nœud extérieur (défaut : même libellé de sous-catégorie)
   @param {(row:object, sub:object) => string} [rowSubtitle] - 2e libellé d'une ligne dépliée
───────────────────────────────────────────── */
const UsageTaxonomySunburstCard = ({
    categoryTotals,
    subcategoryTotals,
    waterPointRows,
    unit,
    headerActions,
    title = "Répartition par catégorie d'usage",
    innerEyebrow = 'Catégorie',
    getRowsForSub,
    rowSubtitle = (row) => row.name_room || '-',
}) => {
    const containerRef = useRef(null);
    const [hovered, setHovered] = useState(null); // {type: 'category'|'subcategory', id, categoryId}
    const [pinned, setPinned] = useState(null); // idem, posé par un clic (persiste hors survol)

    const active = hovered ?? pinned;

    const total = Math.round(categoryTotals.reduce((sum, c) => sum + c.value, 0));

    const categoryColorById = useMemo(() => {
        const colors = colorsAlongGradient(categoryTotals, BREAKDOWN_GRADIENT, (cat) => cat.id === 'uncategorized');
        return Object.fromEntries(categoryTotals.map((cat, i) => [cat.id, colors[i]]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryTotals]);

    const categoryLabelById = useMemo(
        () => Object.fromEntries(categoryTotals.map((c) => [c.id, c.label])),
        [categoryTotals]
    );

    // Le bucket "Autres" (reliquat non matché) existe une fois par catégorie et porte
    // toujours ce même libellé : s'il apparaît pour plus d'une catégorie, ClientBuilding
    // Dashboard.js qualifie son libellé "Autres (Catégorie)" sur waterPointRows — il faut
    // reproduire la même règle ici pour retrouver les bonnes lignes au dépliement. Si la
    // règle de désambiguïsation change, reporter le changement ici aussi.
    const ambiguousSubcategoryLabels = useMemo(() => {
        const counts = {};
        subcategoryTotals.forEach((s) => {
            counts[s.label] = (counts[s.label] || 0) + 1;
        });
        return new Set(Object.keys(counts).filter((label) => counts[label] > 1));
    }, [subcategoryTotals]);

    const qualifiedSubLabel = (sub) =>
        ambiguousSubcategoryLabels.has(sub.label) ? `${sub.label} (${categoryLabelById[sub.categoryId] || ''})` : sub.label;

    const tree = useMemo(() => {
        const countByCategory = {};
        subcategoryTotals.forEach((s) => {
            countByCategory[s.categoryId] = (countByCategory[s.categoryId] || 0) + 1;
        });
        const indexByCategory = {};

        return {
            id: 'root',
            children: categoryTotals.map((cat) => {
                const baseColor = categoryColorById[cat.id] || OTHER_COLOR;
                const children = subcategoryTotals
                    .filter((s) => s.categoryId === cat.id)
                    .map((sub) => {
                        const count = countByCategory[cat.id] || 1;
                        const i = indexByCategory[cat.id] || 0;
                        indexByCategory[cat.id] = i + 1;
                        return { id: sub.id, label: sub.label, value: sub.value, fillColor: subcategoryColor(baseColor, i, count) };
                    });
                return { id: cat.id, label: cat.label, fillColor: baseColor, children };
            }),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryTotals, subcategoryTotals, categoryColorById]);

    // Catégorie active → estompe tout ce qui n'a pas le même categoryId. Sous-catégorie
    // active → ne reste plein que ce nœud précis + l'arc de sa catégorie parente (pour
    // garder le lien visuel avec l'anneau intérieur), tout le reste s'estompe.
    const isDimmed = (targetType, targetId, targetCategoryId) => {
        if (!active) return false;
        if (active.type === 'category') return targetCategoryId !== active.id;
        if (targetType === 'category') return targetId !== active.categoryId;
        return targetId !== active.id;
    };

    const getFill = (node) => {
        const nodeType = node.depth === 1 ? 'category' : 'subcategory';
        const dimmed = isDimmed(nodeType, node.id, categoryIdOf(node));
        return dimmed ? mixColor(node.data.fillColor, DIM_TARGET, 0.55) : node.data.fillColor;
    };

    const chartSize = CHART_RADIUS * 2 + CHART_MARGIN * 2;

    const legendPercent = (value) => (total > 0 ? ((value / total) * 100).toFixed(1) : '0.0');

    const setHoveredTarget = (type, id, catId) => setHovered({ type, id, categoryId: catId });
    const togglePinned = (type, id, catId) =>
        setPinned((prev) => (prev && prev.type === type && prev.id === id ? null : { type, id, categoryId: catId }));

    const legendRowClass = (type, id, catId) => {
        const isActive = Boolean(active && active.type === type && active.id === id);
        const dimmed = isDimmed(type, id, catId);
        return [
            'sunburst-legend-row',
            type === 'category' ? 'sunburst-legend-row--category' : 'sunburst-legend-row--sub',
            isActive ? 'sunburst-legend-row--active' : '',
            dimmed ? 'sunburst-legend-row--dimmed' : '',
        ]
            .filter(Boolean)
            .join(' ');
    };

    // Résout le contenu du survol central à partir de l'état actif (catégorie ou
    // sous-catégorie), quelle que soit sa source (survol du donut ou de la légende).
    const activeDisplay = useMemo(() => {
        if (!active) return null;
        if (active.type === 'category') {
            const cat = categoryTotals.find((c) => c.id === active.id);
            if (!cat) return null;
            return { eyebrow: innerEyebrow, label: cat.label, value: cat.value };
        }
        const sub = subcategoryTotals.find((s) => s.id === active.id);
        if (!sub) return null;
        return { eyebrow: categoryLabelById[sub.categoryId] || 'Sous-catégorie', label: sub.label, value: sub.value };
    }, [active, categoryTotals, subcategoryTotals, categoryLabelById, innerEyebrow]);

    const pinnedSubcategoryRows = useMemo(() => {
        if (!pinned || pinned.type !== 'subcategory' || !waterPointRows) return null;
        const sub = subcategoryTotals.find((s) => s.id === pinned.id);
        if (!sub) return null;
        const label = qualifiedSubLabel(sub);
        const rows = getRowsForSub
            ? getRowsForSub(sub, waterPointRows)
            : waterPointRows.filter((row) => row.usage_subcategory_label === label);
        return [...rows]
            .sort((a, b) => (b.annual_volume || 0) - (a.annual_volume || 0));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pinned, waterPointRows, subcategoryTotals, ambiguousSubcategoryLabels, categoryLabelById, getRowsForSub]);

    const maxPinnedRowVolume = pinnedSubcategoryRows?.length ? (pinnedSubcategoryRows[0].annual_volume || 0) : 0;

    return (
        <ChartCard title={title} bodyClassName="sunburst-card-body" actions={headerActions}>
            <div className="sunburst-chart-area" ref={containerRef} style={{ width: chartSize, height: chartSize }}>
                <ResponsiveSunburst
                    data={tree}
                    id="id"
                    value="value"
                    valueFormat={(v) => `${formatValue(v)} ${unit}`}
                    margin={{ top: CHART_MARGIN, right: CHART_MARGIN, bottom: CHART_MARGIN, left: CHART_MARGIN }}
                    cornerRadius={3}
                    borderWidth={2}
                    borderColor="#ffffff"
                    colors={getFill}
                    inheritColorFromParent={false}
                    enableArcLabels={false}
                    animate={true}
                    motionConfig="gentle"
                    transitionMode="pushIn"
                    isInteractive={true}
                    theme={{ text: { fontFamily: FONT_STACK } }}
                    onMouseEnter={(node) => setHoveredTarget(node.depth === 1 ? 'category' : 'subcategory', node.id, categoryIdOf(node))}
                    onMouseLeave={() => setHovered(null)}
                    onClick={(node) => togglePinned(node.depth === 1 ? 'category' : 'subcategory', node.id, categoryIdOf(node))}
                    tooltip={(node) => {
                        const isSub = node.depth === 2;
                        return (
                            <div className="sunburst-tooltip">
                                <div className="sunburst-tooltip-head">
                                    <span className="sunburst-tooltip-swatch" style={{ background: node.data.fillColor }} />
                                    <div>
                                        {isSub && <div className="sunburst-tooltip-breadcrumb">{node.parent?.data?.label}</div>}
                                        <div className="sunburst-tooltip-label">{node.data.label}</div>
                                    </div>
                                </div>
                                <div className="sunburst-tooltip-value">
                                    {formatValue(node.value)} {unit}
                                </div>
                                <div className="sunburst-tooltip-bar-track">
                                    <div
                                        className="sunburst-tooltip-bar-fill"
                                        style={{ width: `${node.percentage}%`, background: node.data.fillColor }}
                                    />
                                </div>
                                <div className="sunburst-tooltip-percent">{node.percentage.toFixed(1)}% du total</div>
                                {isSub && <div className="sunburst-tooltip-hint">Cliquer pour explorer les usages</div>}
                            </div>
                        );
                    }}
                />

                <div className="sunburst-center-overlay">
                    {activeDisplay ? (
                        <>
                            <div className="sunburst-center-eyebrow">{activeDisplay.eyebrow}</div>
                            <div className="sunburst-center-label">{activeDisplay.label}</div>
                            <div className="sunburst-center-value">
                                {formatValue(activeDisplay.value)} {unit}
                            </div>
                            <div className="sunburst-center-eyebrow">{legendPercent(activeDisplay.value)}%</div>
                        </>
                    ) : (
                        <>
                            <div className="sunburst-center-value sunburst-center-value--total">
                                {formatValue(total)} {unit}
                            </div>
                            <div className="sunburst-center-eyebrow">Total</div>
                        </>
                    )}
                </div>
            </div>

            <div className="sunburst-legend">
                <div className="sunburst-legend-header">Légende</div>
                <div className="sunburst-legend-grid">
                    {categoryTotals.map((cat) => {
                        const catColor = categoryColorById[cat.id] || OTHER_COLOR;
                        return (
                            <React.Fragment key={cat.id}>
                                <div
                                    className={legendRowClass('category', cat.id, cat.id)}
                                    onMouseEnter={() => setHoveredTarget('category', cat.id, cat.id)}
                                    onMouseLeave={() => setHovered(null)}
                                    onClick={() => togglePinned('category', cat.id, cat.id)}
                                >
                                    <span className="sunburst-legend-swatch-label">
                                        <span className="sunburst-legend-swatch" style={{ background: catColor }} />
                                        <span className="sunburst-legend-name">{cat.label}</span>
                                    </span>
                                    <span className="sunburst-legend-value">
                                        {formatValue(cat.value)} {unit}
                                    </span>
                                    <span className="sunburst-legend-percent">{legendPercent(cat.value)}%</span>
                                </div>
                                {subcategoryTotals
                                    .filter((s) => s.categoryId === cat.id)
                                    .map((sub, i, arr) => {
                                        const subColor = subcategoryColor(catColor, i, arr.length);
                                        const isPinnedOpen = pinned && pinned.type === 'subcategory' && pinned.id === sub.id;
                                        return (
                                            <React.Fragment key={sub.id}>
                                                <div
                                                    className={legendRowClass('subcategory', sub.id, sub.categoryId)}
                                                    onMouseEnter={() => setHoveredTarget('subcategory', sub.id, sub.categoryId)}
                                                    onMouseLeave={() => setHovered(null)}
                                                    onClick={() => togglePinned('subcategory', sub.id, sub.categoryId)}
                                                >
                                                    <span className="sunburst-legend-swatch-label">
                                                        <span
                                                            className="sunburst-legend-swatch"
                                                            style={{ background: subColor }}
                                                        />
                                                        <span className="sunburst-legend-chevron">{isPinnedOpen ? '▾' : '▸'}</span>
                                                        <span className="sunburst-legend-name">{sub.label}</span>
                                                    </span>
                                                    <span className="sunburst-legend-value">
                                                        {formatValue(sub.value)} {unit}
                                                    </span>
                                                    <span className="sunburst-legend-percent">{legendPercent(sub.value)}%</span>
                                                </div>
                                                {isPinnedOpen && (
                                                    <div
                                                        className="sunburst-legend-expand"
                                                        style={{ '--cbd-accent': subColor }}
                                                    >
                                                        <div className="cbd-point-list">
                                                            {!pinnedSubcategoryRows || pinnedSubcategoryRows.length === 0 ? (
                                                                <p className="cbd-empty">Aucun usage enregistré pour cette sélection.</p>
                                                            ) : (
                                                                pinnedSubcategoryRows.map((row, idx) => {
                                                                    const pct = maxPinnedRowVolume
                                                                        ? ((row.annual_volume || 0) / maxPinnedRowVolume) * 100
                                                                        : 0;
                                                                    return (
                                                                        <div className="cbd-point-row" key={`${row.equipment_id}-${idx}`}>
                                                                            <div className="cbd-point-main">
                                                                                <span className="cbd-point-type">
                                                                                    {EQUIPMENT_TYPE_LABELS[row.type_equipment] || row.type_equipment}
                                                                                </span>
                                                                                <span className="cbd-point-room">{rowSubtitle(row, sub)}</span>
                                                                            </div>
                                                                            <span
                                                                                className="letter-rate-badge cbd-point-badge"
                                                                                style={{
                                                                                    backgroundColor: RATING_LETTER_COLORS[row.rating_letter] || '#ccc',
                                                                                }}
                                                                            >
                                                                                {row.rating_letter || '-'}
                                                                            </span>
                                                                            <div className="cbd-point-gauge-wrapper">
                                                                                <div className="cbd-point-gauge-fill" style={{ width: `${pct}%` }} />
                                                                            </div>
                                                                            <span className="cbd-point-value">
                                                                                {formatValue(row.annual_volume)} {unit}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </ChartCard>
    );
};

export default UsageTaxonomySunburstCard;
